import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";

@Injectable({
	providedIn: "root",
})
export class KioskService {
	/**
	 * Initializes kiosk mode and subscribes to app lifecycle events.
	 *
	 * Enters kiosk mode immediately and registers a callback for app state
	 * changes so that kiosk mode is re-entered when the app resumes from background.
	 */
	initialize(): void {
		if (environment.enableKioskOnResume) {
			this.enable();
		}
	}

	/**
	 * Enables kiosk mode (enters lock task mode).
	 *
	 * Puts the device into kiosk/lock task mode, restricting user access
	 * to the application.
	 */
	async enable(): Promise<void> {
		try {
			const { Kiosk } = await import("@nettask/capacitor-kiosk");
			await Kiosk.enable();
		} catch (error) {
			console.error("[Kiosk] Failed to enable kiosk", error);
		}
	}

	/**
	 * Temporarily exits kiosk mode.
	 *
	 * Releases the kiosk mode lock of the device manager, allowing to navigate away
	 * from the app for example into the settings. The lock is automatically re-enabled when the app resumes.
	 */
	async exit(): Promise<void> {
		try {
			const { Kiosk } = await import("@nettask/capacitor-kiosk");
			await Kiosk.exit();
		} catch (error) {
			console.error("[Kiosk] Failed to exit kiosk", error);
		}
	}

	/**
	 * Disables kiosk mode and removes device owner privileges.
	 *
	 * This fully exits kiosk mode AND removes the app's device owner
	 * status. After calling this, the app will no longer be able to
	 * control device behavior or re-enter lock task mode.
	 */
	async disable(): Promise<void> {
		try {
			const { Kiosk } = await import("@nettask/capacitor-kiosk");
			await Kiosk.disable();
		} catch (error) {
			console.error("[Kiosk] Failed to disable kiosk", error);
		}
	}

	//TODO: Add method to check if kiosk mode is currently active
	//TODO: Add callback registry for changing states (only if future features need this)
	//TODO: Add method to disable USB debugging/ADB or do it directly in the plugin
}
